import React, { useState } from 'react';
import { useSession } from '../../context/SessionContext';
import { useNotification } from '../../context/NotificationContext';
import { FaCircleInfo } from "react-icons/fa6";
import './ActionButtonWrapper.less';
import { CaptureEmail, RatingModal } from '../../components';
import { COMMAND_TYPES } from '../../constants/commandTypes';
import { STEPS } from '../../constants/steps';
import { sendCommand } from '../../api/evControl';
import { forceEndSession, stopSession as apiStopSession } from '../../api/sessionsApi';
import { clearActiveSession } from '../../utils/sessionPersistence';
import { PAYMENTS_ENABLED } from '../../config/features';
import { translateStopError } from '../../utils/stopSessionErrorMessages';

const ActionButtonWrapper = ({ actions = [], onClickAction = () => {}, onCaptureOpenChange, onReceiptRequested, ref = null }) => {
    const { reset, setStep, update, state } = useSession();
    const { notify } = useNotification();
    const [showCapture, setShowCapture] = useState(false);
    const [showRating, setShowRating] = useState(false);
    const requireFields = (obj, fields, label) => {
        const missing = fields.filter(f => obj[f] === undefined || obj[f] === null || obj[f] === '');

        if (missing.length) throw new Error(`${label}: missing ${missing.join(', ')}`);
    };

    const handleClick = async (action) => {
        // If action has a custom onClick handler, use it
        if (action.onClick && typeof action.onClick === 'function') {
            action.onClick();
            return;
        }

        switch (action.id) {
            case 'cancel':
                notify({
                    title: (
                        <span>
                            <FaCircleInfo style={{ marginRight: '.5rem' }} />
                            Are you sure?
                        </span>
                    ),
                    message: "This will cancel and reset your session.",
                    action: {
                        label: "Yes",
                        onClick: () => {
                            reset();
                            setStep(1);
                        }
                    }
                });
                break;

            case 'stop-charging':
                notify({
                    title: (
                        <span>
                            <FaCircleInfo style={{ marginRight: '.5rem' }} />
                            Are you sure?
                        </span>
                    ),
                    message: "This will stop your charging session.",
                    action: {
                        label: "Yes",
                        onClick: async () => {
                            // Authoritative customer stop path.
                            //
                            // Previous version called sendCommand(REMOTE_STOP_TRANSACTION)
                            // which hits /control/stop — an admin-gated route
                            // (authRequired + requireRole('admin') in
                            // sw-ev-api/routes/control.js). A real customer got 401/403,
                            // read as "Charger Not Responding," which then made Force End
                            // the only option — while the backend session was still
                            // sitting in 'charging' status, guaranteeing the guard would
                            // reject the subsequent Force End with OCPP_SESSION_STILL_ACTIVE.
                            //
                            // The customer-authenticated path
                            // POST /api/ev/sessions/:sessionUid/stop
                            // sets stop_requested_at, transitions status → 'stopping',
                            // dispatches RemoteStopTransaction with a 10s timeout, and
                            // returns before waiting for the charger to confirm. We do
                            // NOT navigate on that dispatch success — the UI stays on
                            // ChargeMonitoring in a "Stopping charge…" state and only
                            // advances on backend-terminal status (ChargeMonitoring Part 6
                            // handles that poll + advance to SUMMARY).
                            if (!state?.sessionUid) {
                                console.error('[Stop] Missing sessionUid — cannot stop');
                                notify({
                                    title: "Session not ready",
                                    message: "We couldn't find your session. Please refresh and try again.",
                                });
                                return;
                            }

                            // Mark endTime immediately so ChargeMonitoring can render
                            // the "Stopping charge…" state and reveal Force End after
                            // its existing 30s timer. Success advance is driven by the
                            // backend poll, not by this dispatch.
                            update({
                                live: {
                                    ...state.live,
                                    endTime: state.live?.endTime || new Date().toISOString(),
                                },
                            });

                            let result;
                            try {
                                result = await apiStopSession(state.sessionUid);
                            } catch (err) {
                                console.error('[Stop] Unexpected throw from apiStopSession:', err);
                                result = { ok: false, error: 'INTERNAL_ERROR', message: err?.message || String(err) };
                            }

                            if (result?.ok) {
                                // Backend accepted the stop dispatch. Stay on
                                // ChargeMonitoring — do NOT setStep to
                                // DISCONNECT_CHARGER here. ChargeMonitoring's Part 6
                                // poll watches for backend-terminal status
                                // (finalized_at, completed, stopped, finalized) and
                                // advances the customer to the summary only when
                                // the session is truly done. The "Stopping charge…"
                                // treatment inside DisconnectCharger explains what
                                // we're waiting for; Force End remains available
                                // via the existing showForceEnd trigger in
                                // ChargeMonitoring.
                                console.log('[Stop] Backend accepted stop dispatch — waiting for terminal confirmation');
                                return;
                            }

                            // Backend rejected — translate + surface with the
                            // right recovery affordance.
                            const translated = translateStopError(result);
                            const isMissingSession = result?.status === 404
                                || result?.error === 'SESSION_NOT_FOUND';

                            if (isMissingSession) {
                                notify({
                                    title: 'No Active Session',
                                    message: translated.message,
                                    action: {
                                        label: 'OK',
                                        onClick: () => {
                                            reset();
                                            setStep(STEPS.EV_PROFILE);
                                        },
                                    },
                                });
                                return;
                            }

                            // Everything else — network / OCPP / server — offers
                            // Force End as the recovery path. Do NOT navigate away
                            // from ChargeMonitoring; the "Stopping charge…" state
                            // stays visible with Force End available.
                            notify({
                                title: translated.title,
                                message: translated.message,
                                action: {
                                    label: 'Force End Session',
                                    onClick: async () => {
                                        let forceResult;
                                        try {
                                            forceResult = await forceEndSession(state.sessionUid);
                                        } catch (forceErr) {
                                            console.error('[ForceEnd] Unexpected throw:', forceErr);
                                            forceResult = { ok: false, error: 'INTERNAL_ERROR', message: forceErr?.message };
                                        }
                                        if (forceResult?.ok) {
                                            // Do NOT navigate to SUMMARY yet — the
                                            // backend has just closed the billing
                                            // session but finalization + physical
                                            // unplug may still be pending. The
                                            // ChargeMonitoring Part 6 poll will
                                            // advance to SUMMARY when the row is
                                            // truly terminal.
                                            update({
                                                live: {
                                                    ...state.live,
                                                    endTime: state.live?.endTime || new Date().toISOString(),
                                                },
                                            });
                                            notify({
                                                title: 'Billing session ended',
                                                message: 'We finalized your billing using the last available charger data. Please unplug the vehicle if you have not already.',
                                            });
                                        } else {
                                            const t = translateStopError(forceResult);
                                            notify({ title: t.title, message: t.message });
                                        }
                                    },
                                },
                            });
                        }
                    }
                });
                break;

            case 'force-end':
                notify({
                    title: (
                        <span>
                            <FaCircleInfo style={{ marginRight: '.5rem' }} />
                            Force end session?
                        </span>
                    ),
                    message:
                        "This ends your billing session using the last available charger data. If the vehicle is still charging you'll need to unplug it manually.",
                    action: {
                        label: "Force End",
                        onClick: async () => {
                            let result;
                            try {
                                result = await forceEndSession(state.sessionUid);
                            } catch (err) {
                                console.error('[ForceEnd] Unexpected throw:', err);
                                result = { ok: false, error: 'INTERNAL_ERROR', message: err?.message };
                            }
                            if (result?.ok) {
                                // Backend has closed the billing session and
                                // best-effort finalized. Do NOT skip to SUMMARY
                                // — the ChargeMonitoring Part 6 poll owns the
                                // "session truly terminal" gate. Advance only
                                // when the row is confirmed terminal.
                                update({
                                    live: {
                                        ...state.live,
                                        endTime: state.live?.endTime || new Date().toISOString(),
                                    }
                                });
                                notify({
                                    title: 'Billing session ended',
                                    message: 'We finalized your billing using the last available charger data. Please unplug the vehicle if you have not already.',
                                });
                            } else {
                                const t = translateStopError(result);
                                notify({ title: t.title, message: t.message });
                            }
                        }
                    }
                });
                break;

            case 'start-charging':
                // Guard: Prevent starting session when payment is required but not authorized
                // Check session.vehicle.paymentRequired to respect billing mode (FREE, FLEET_INVOICE don't need payment)
                const paymentRequired = state.session?.vehicle?.paymentRequired === true;
                const billingMode = state.session?.vehicle?.billingMode || 'UNKNOWN';
                
                if (PAYMENTS_ENABLED && paymentRequired && !state.payment?.authorizationId) {
                    console.warn('[ActionButtonWrapper] Payment required but missing', {
                        paymentRequired,
                        billingMode,
                        hasAuthorizationId: !!state.payment?.authorizationId
                    });
                    notify({
                        title: 'Payment Required',
                        message: 'Please complete payment authorization before starting your charging session.',
                        action: {
                            label: 'OK',
                            onClick: () => {
                                // Navigate back to payment step
                                setStep(STEPS.AMOUNT_PICKER);
                            }
                        }
                    });
                    return;
                }
                
                // Log payment check result for billing modes that don't require payment
                if (PAYMENTS_ENABLED && !paymentRequired) {
                    console.log('[ActionButtonWrapper] paymentRequired=false, billingMode=' + billingMode + ', allowing start without payment auth');
                }

                const { stationId, connectorId } = action.data || {};
                const payload = { stationId, connectorId };
                requireFields(payload, ['stationId', 'connectorId'], 'REMOTE_START_TRANSACTION');
                
                try {
                    const res = await sendCommand(COMMAND_TYPES.REMOTE_START_TRANSACTION, payload);
                    console.log(' Charging started successfully:', res);
                } catch (error) {
                    console.error(' Error starting charging session:', error);
                    notify({
                        title: 'Error Starting Session',
                        message: error.message || 'Failed to start charging session. Please try again.',
                        action: {
                            label: 'OK',
                            onClick: () => {}
                        }
                    });
                }
                break;
            case 'get-receipt':
                setShowCapture(true);
                break;
            
            case 'rate-experience':
                setShowRating(true);
                break;

            default:
                // Pass only the action id to the parent handler
                onClickAction(action.id);
                break;
        }
    };

    return (
        <>
            <div ref={ref} className="action-button-wrapper">
                {actions.map((action, index) => (
                    <button
                        key={action.id ?? index}
                        className={`btn ${action.type}`}
                        onClick={() => handleClick(action)}
                        disabled={action.disabled ?? false}>
                        {action.label}
                    </button>
                ))}
            </div>

            <CaptureEmail
                show={showCapture}
                onClose={() => setShowCapture(false)}
                onOpenChange={onCaptureOpenChange}
                onResult={onReceiptRequested}
            />
            
            <RatingModal
                show={showRating}
                onClose={() => setShowRating(false)}
            />
        </>
    );
};

export default ActionButtonWrapper;
